import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, TimePicker, Select, Segmented, Popconfirm, message } from 'antd';
import { Plus, Video, MapPin, Users, Trash2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { agendaOf, meetingState, useMeetings, type Meeting } from './rightPanelData';

interface MeetingForm { title: string; date: Dayjs; time: [Dayjs, Dayjs]; mode: 'online' | 'offline'; link?: string; location?: string; attendees: string[]; note?: string }

/** Form tạo cuộc họp (dùng cho nút "Tạo cuộc họp" và dấu + của Lịch làm việc) */
export function MeetingModal({ open, meId, onClose }: { open: boolean; meId: string; onClose: () => void }) {
  const [form] = Form.useForm<MeetingForm>();
  const { add } = useMeetings(meId);
  const start = dayjs().add(1, 'hour').startOf('hour');

  const submit = (v: MeetingForm) => {
    const s = v.date.hour(v.time[0].hour()).minute(v.time[0].minute()).second(0);
    const e = v.date.hour(v.time[1].hour()).minute(v.time[1].minute()).second(0);
    add({
      title: v.title.trim(), start: s.toISOString(), end: e.toISOString(),
      link: v.mode === 'online' ? v.link?.trim() : undefined, location: v.mode === 'offline' ? v.location?.trim() : undefined,
      attendees: v.attendees || [], note: v.note?.trim() || undefined, createdBy: meId,
    });
    message.success(`Đã tạo cuộc họp lúc ${s.format('HH:mm DD/MM')}`);
    onClose();
  };

  return (
    <Modal open={open} title="Tạo cuộc họp" okText="Tạo cuộc họp" cancelText="Hủy" onCancel={onClose} onOk={() => form.submit()} destroyOnHidden width={520}>
      <Form form={form} layout="vertical" onFinish={submit} preserve={false}
        initialValues={{ date: start, time: [start, start.add(30, 'minute')], mode: 'online', attendees: [] }}>
        <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, whitespace: true, message: 'Nhập tiêu đề cuộc họp' }]}>
          <Input maxLength={120} placeholder="VD: Họp giao ban tuần" autoFocus />
        </Form.Item>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Form.Item name="date" label="Ngày" rules={[{ required: true, message: 'Chọn ngày' }]}>
            <DatePicker format="DD/MM/YYYY" className="w-full" allowClear={false} disabledDate={d => d.isBefore(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="time" label="Thời gian" dependencies={['date']}
            rules={[
              { required: true, message: 'Chọn giờ bắt đầu và kết thúc' },
              ({ getFieldValue }) => ({
                validator: (_, v?: [Dayjs, Dayjs]) => {
                  if (!v) return Promise.resolve();
                  if (!v[1].isAfter(v[0])) return Promise.reject(new Error('Giờ kết thúc phải sau giờ bắt đầu'));
                  const d: Dayjs = getFieldValue('date');
                  if (d && d.hour(v[0].hour()).minute(v[0].minute()).isBefore(dayjs())) return Promise.reject(new Error('Thời gian bắt đầu đã qua'));
                  return Promise.resolve();
                },
              }),
            ]}>
            <TimePicker.RangePicker format="HH:mm" minuteStep={5} className="w-full" allowClear={false} needConfirm={false} />
          </Form.Item>
        </div>
        <Form.Item name="mode" label="Hình thức">
          <Segmented options={[{ label: 'Họp online', value: 'online' }, { label: 'Tại văn phòng', value: 'offline' }]} />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(a, b) => a.mode !== b.mode}>
          {({ getFieldValue }) => (getFieldValue('mode') === 'online' ? (
            <Form.Item name="link" label="Link họp" rules={[{ type: 'url', message: 'Link chưa đúng định dạng (https://...)' }]}>
              <Input placeholder="https://meet.google.com/..." />
            </Form.Item>
          ) : (
            <Form.Item name="location" label="Địa điểm" rules={[{ required: true, whitespace: true, message: 'Nhập địa điểm họp' }]}>
              <Input placeholder="VD: Phòng họp tầng 2" maxLength={80} />
            </Form.Item>
          ))}
        </Form.Item>
        <Form.Item name="attendees" label="Thành phần tham dự">
          <Select mode="tags" open={false} suffixIcon={null} placeholder="Gõ tên người / bộ phận rồi Enter" tokenSeparators={[',']} />
        </Form.Item>
        <Form.Item name="note" label="Nội dung">
          <Input.TextArea rows={2} maxLength={500} placeholder="Chương trình họp, tài liệu cần chuẩn bị..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const STATE_UI = {
  live: { bar: 'bg-red-500', badge: 'Đang diễn ra', badgeCls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300' },
  soon: { bar: 'bg-amber-500', badge: 'Sắp bắt đầu', badgeCls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
  upcoming: { bar: 'bg-blue-500', badge: '', badgeCls: '' },
  past: { bar: 'bg-gray-300', badge: '', badgeCls: '' },
};

const WEEKDAY = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const dayLabel = (d: Dayjs) => (d.isSame(dayjs(), 'day') ? 'Hôm nay' : d.isSame(dayjs().add(1, 'day'), 'day') ? 'Ngày mai' : `${WEEKDAY[d.day()]} ${d.format('DD/MM')}`);

/** Lịch làm việc: cuộc họp hôm nay và 7 ngày tới */
export function ScheduleWidget({ meId, onCreate }: { meId: string; onCreate: () => void }) {
  const navigate = useNavigate();
  const { list, remove } = useMeetings(meId);
  const [now, setNow] = useState(dayjs());
  useEffect(() => { const t = setInterval(() => setNow(dayjs()), 60_000); return () => clearInterval(t); }, []);
  const agenda = agendaOf(list, now);
  const shown = agenda.slice(0, 4);

  let lastDay = '';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Lịch làm việc</h3>
        <button onClick={onCreate} title="Tạo cuộc họp" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="px-4 py-3 space-y-3">
        {shown.length === 0 && <div className="text-sm text-gray-500 text-center py-2">Không có lịch họp trong 7 ngày tới.</div>}
        {shown.map((m: Meeting) => {
          const st = meetingState(m, now);
          const ui = STATE_UI[st];
          const day = dayLabel(dayjs(m.start));
          const header = day !== lastDay ? day : null;
          lastDay = day;
          return (
            <div key={m.id}>
              {header && <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5 first-letter:uppercase">{header}</div>}
              <div className="relative pl-3 flex items-start justify-between gap-2 group">
                <div className={`absolute left-0 top-1 bottom-1 w-0.5 rounded-full ${ui.bar}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-gray-800 dark:text-gray-200 truncate" title={m.title}>{m.title}</div>
                  <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>{dayjs(m.start).format('HH:mm')} – {dayjs(m.end).format('HH:mm')}</span>
                    {ui.badge && <span className={`px-1.5 rounded font-bold ${ui.badgeCls}`}>{ui.badge}</span>}
                  </div>
                  {(m.location || m.attendees.length > 0) && (
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate flex items-center gap-1">
                      {m.location ? <><MapPin className="w-3 h-3" /> {m.location}</> : <><Users className="w-3 h-3" /> {m.attendees.join(', ')}</>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {m.link && (
                    <a href={m.link} target="_blank" rel="noopener noreferrer" title="Vào họp"
                      className={`w-7 h-7 rounded border-2 flex items-center justify-center transition ${st === 'live' || st === 'soon' ? 'border-green-500 bg-green-500 text-white' : 'border-green-500 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'}`}>
                      <Video className="w-3 h-3" />
                    </a>
                  )}
                  {m.createdBy === meId && (
                    <Popconfirm title="Huỷ cuộc họp này?" okText="Huỷ họp" cancelText="Không" okButtonProps={{ danger: true }} onConfirm={() => { remove(m.id); message.success('Đã huỷ cuộc họp'); }}>
                      <button title="Huỷ cuộc họp" className="w-7 h-7 rounded flex items-center justify-center text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></button>
                    </Popconfirm>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {agenda.length > shown.length && <div className="text-[11px] text-gray-400">+ {agenda.length - shown.length} cuộc họp khác trong tuần</div>}
      </div>
      <button type="button" onClick={() => navigate('/appointments')}
        className="w-full px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 text-xs font-bold text-green-600 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-center gap-1 cursor-pointer">
        Xem lịch hẹn khách <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
}
